import { useState } from "react";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";

interface Props { blockId: string; onClose: () => void; }

const OPTIONS: { value: "8h" | "24h" | "3d"; icon: string; label: string; sub: string }[] = [
  { value: "8h", icon: "ri-time-line", label: "8時間", sub: "短期集中オークション" },
  { value: "24h", icon: "ri-24-hours-line", label: "24時間", sub: "スタンダード" },
  { value: "3d", icon: "ri-calendar-line", label: "3日間", sub: "じっくり高値を狙う" },
];

export default function AuctionSetupModal({ blockId, onClose }: Props) {
  const { listForAuction } = useGame();
  const { user } = useAuth();
  const { t } = useLang();
  const [selected, setSelected] = useState<"8h" | "24h" | "3d">("24h");
  const [msg, setMsg] = useState("");

  const handleSubmit = () => {
    if (!user) return;
    const ok = listForAuction(blockId, selected, user.id);
    if (ok) { setMsg(t("auctionSuccess")); setTimeout(onClose, 1200); }
    else { setMsg("出品に失敗しました"); }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>
      <div className="relative bg-[#0f1015] border border-white/10 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center"><i className="ri-auction-line text-amber-400 text-base"></i></div>
            <span className="text-white font-semibold text-sm whitespace-nowrap">{t("auctionTitle")}</span>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-gray-400 cursor-pointer">
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-gray-400 text-sm">{t("auctionDuration")}</p>
          <div className="grid grid-cols-3 gap-3">
            {OPTIONS.map((o) => (
              <button key={o.value} onClick={() => setSelected(o.value)}
                className={`flex flex-col items-center p-4 rounded-xl border transition-all cursor-pointer ${selected === o.value ? "bg-amber-400/15 border-amber-400/50 shadow-lg shadow-amber-400/10" : "bg-white/3 border-white/10 hover:border-white/20"}`}>
                <div className="w-8 h-8 flex items-center justify-center mb-2">
                  <i className={`${o.icon} text-xl ${selected === o.value ? "text-amber-400" : "text-gray-500"}`}></i>
                </div>
                <span className={`text-lg font-black whitespace-nowrap font-['Space_Grotesk',sans-serif] ${selected === o.value ? "text-amber-400" : "text-white"}`}>{o.label}</span>
                <span className="text-gray-500 text-xs mt-1 text-center leading-tight">{o.sub}</span>
              </button>
            ))}
          </div>
          <div className="bg-white/5 rounded-xl p-3">
            <p className="text-gray-400 text-xs">開始価格: <span className="text-white font-semibold">100 coins</span> (購入時の金額)</p>
          </div>
          {msg && <div className={`rounded-xl px-3 py-2 text-xs text-center ${msg.includes("しました") ? "bg-emerald-400/10 text-emerald-400" : "bg-red-400/10 text-red-400"}`}>{msg}</div>}
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 border border-white/10 hover:border-white/20 text-gray-400 font-medium rounded-xl py-3 text-sm transition-all cursor-pointer whitespace-nowrap">
              {t("cancel")}
            </button>
            <button onClick={handleSubmit} className="flex-1 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-black font-bold rounded-xl py-3 text-sm transition-all cursor-pointer whitespace-nowrap">
              {t("startAuction")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
